import { FC } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  StyledTable,
  StyledTableHeaderRow,
  StyledTableHeaderCell,
  StyledTableRow,
  StyledTableCell,
  StyledTableWrapper,
} from './Table.styles';
import { range } from 'ramda';
import ChevronDown from '../../icons/ChevronDown';
import ChevronUp from '../../icons/ChevronUp';

export interface TableProps {
  headers: (string | JSX.Element | HeaderItem)[];
  data: (string | JSX.Element)[][];
  loading?: boolean;
  updating?: boolean;
  emptyText?: string;
  sortHandlers?: { [key: string]: () => void };
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

/**
A thin wrapper around a table element for styling and accessibility purposes.
*/

type HeaderItem = {
  key: string;
  display: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isHeaderItem(item: any): item is HeaderItem {
  return typeof item === 'object' && 'key' in item && 'display' in item;
}
export const Table: FC<TableProps> = ({
  headers,
  data,
  loading,
  updating,
  emptyText,
  sortHandlers = {},
  sortField,
  sortDirection,
}) => {
  return (
    <StyledTableWrapper>
      <StyledTable>
        <thead>
          <StyledTableHeaderRow>
            {headers.map((header, i) => {
              const headerKey = isHeaderItem(header) ? header.key : null;
              const displayText = isHeaderItem(header)
                ? header.display
                : header;

              return (
                <StyledTableHeaderCell
                  onClick={() => {
                    if (headerKey && sortHandlers[headerKey]) {
                      sortHandlers[headerKey]();
                    }
                  }}
                  key={i}
                  style={headerKey ? { cursor: 'pointer' } : undefined} // Pointer cursor only for HeaderItem
                >
                  <div style={{ display: 'flex' }}>
                    {displayText}
                    <span>
                      {headerKey === sortField ? (
                        sortDirection === 'asc' ? (
                          <ChevronUp width={20} height={20} />
                        ) : (
                          <ChevronDown width={20} height={20} />
                        )
                      ) : (
                        // Use a transparent placeholder for non-sort fields
                        <ChevronUp
                          width={20}
                          height={20}
                          style={{ visibility: 'hidden' }}
                        />
                      )}
                    </span>
                  </div>
                </StyledTableHeaderCell>
              );
            })}
          </StyledTableHeaderRow>
        </thead>
        <tbody
          style={{
            opacity: updating ? 0.5 : 1,
            pointerEvents: updating ? 'none' : 'auto',
          }}
        >
          {loading ? (
            range(0, 4).map((i) => (
              <StyledTableRow key={i}>
                {range(0, headers.length).map((j) => (
                  <StyledTableCell key={j}>
                    <Skeleton
                      style={{
                        width: `${80 / (headers.length || 1)}vw`,
                        height: '0.8em',
                      }}
                    />
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))
          ) : data.length ? (
            data.map((row, i) => (
              <StyledTableRow key={i}>
                {row.map((cell, j) => (
                  <StyledTableCell key={j}>{cell}</StyledTableCell>
                ))}
              </StyledTableRow>
            ))
          ) : (
            <StyledTableRow>
              <StyledTableCell colSpan={headers.length}>
                {emptyText || 'No data'}
              </StyledTableCell>
            </StyledTableRow>
          )}
        </tbody>
      </StyledTable>
    </StyledTableWrapper>
  );
};

export default Table;
